/* The session: the same Jackdesk token every app holds, under the same keys.
 *
 * A person signed in to any Jackdesk app is signed in here — there is no
 * second password. No app decides what a token allows; the gateway checks it
 * with `jackdesk_auth` on every call, so nothing here is a security decision,
 * only a convenience one.
 */
import { clearIdentity, isStaleIdentity } from './identity'

const TOKEN_KEY = 'jackdesk_token'
const PROFILE_KEY = 'jackdesk_profile'
const RETURN_KEY = 'jackdesk_return_to'

export type Profile = {
  access: string
  refresh?: string
  user: {
    id: number
    email?: string
    first_name?: string
    last_name?: string
    organization_id?: number
    user_type?: string
  }
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch { return null }
}

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY) } catch { return null }
}

export function getProfile(): Profile | null { return read<Profile>(PROFILE_KEY) }

export function saveSession(profile: Profile | null): void {
  try {
    if (profile) {
      localStorage.setItem(TOKEN_KEY, profile.access)
      localStorage.setItem(PROFILE_KEY, JSON.stringify(profile))
    } else {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(PROFILE_KEY)
    }
  } catch { /* private window: the session lasts this tab */ }
}

export function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    // A JWT is UTF-8; `atob` gives bytes, so a non-ASCII name needs decoding
    // rather than being shown as mojibake in the corner of every screen.
    return JSON.parse(decodeURIComponent(
      Array.from(json, (c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('')))
  } catch { return null }
}

/** Expired as far as this device is concerned. The gateway decides for real. */
export function isExpired(token: string): boolean {
  const exp = decodeJwt(token)?.exp
  return typeof exp === 'number' ? Date.now() >= exp * 1000 : false
}

export function isSignedIn(): boolean {
  const token = getToken()
  if (!token || isExpired(token)) return false
  return !isStaleIdentity(getProfile()?.user?.id)
}

/**
 * A token handed over in the address bar (`?token=`, from the store or
 * another app) is taken **once** and removed, so it is never bookmarked, never
 * kept in history, and never sent on in a `Referer` header. Runs before React
 * mounts, which is what makes the hand-off silent.
 */
export function consumeTokenFromUrl(): boolean {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token')
  if (!token) return false
  const claims = decodeJwt(token) ?? {}
  const kept = getProfile()
  const id = Number(claims.user_id ?? claims.id ?? -1)
  saveSession({
    access: token,
    refresh: params.get('refresh') ?? (kept?.user?.id === id ? kept?.refresh : undefined),
    user: kept && kept.user?.id === id ? kept.user : {
      id,
      email: claims.email as string | undefined,
      organization_id: claims.organization_id as number | undefined,
      user_type: claims.user_type as string | undefined,
    },
  })
  params.delete('token'); params.delete('refresh')
  const query = params.toString()
  window.history.replaceState({}, '',
    window.location.pathname + (query ? `?${query}` : '') + window.location.hash)
  return true
}

export function rememberReturnTo(
  path = window.location.pathname + window.location.search,
): void {
  try { sessionStorage.setItem(RETURN_KEY, path) } catch { /* private window */ }
}

/** Where they were heading before signing in — only ever a path on this
 *  origin, so a crafted `?next=` cannot bounce anybody off the product. */
export function consumeReturnTo(): string | null {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY)
    sessionStorage.removeItem(RETURN_KEY)
    if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null
    return raw
  } catch { return null }
}

/** The session is over. Remember where they were, then show sign-in. */
export function endSession(): void {
  rememberReturnTo()
  saveSession(null)
  window.location.replace('/signin')
}

export async function signOut(authBase: string): Promise<void> {
  const profile = getProfile()
  try {
    if (profile?.refresh) {
      // Deliberately no Authorization header: jackdesk_auth refuses a logout
      // from a caller presenting a valid access token.
      await fetch(`${authBase}/auth/logout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: profile.refresh }), keepalive: true,
      })
    }
  } catch { /* offline: clear locally anyway */ }
  try { localStorage.clear(); sessionStorage.clear() } catch { /* ignore */ }
  clearIdentity()
  window.location.replace('/signin')
}
